import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faBuilding,
  faMoneyBillTrendUp,
  faShieldHalved,
  faUser,
  faUserLock,
} from "@fortawesome/pro-regular-svg-icons";
import { SortingState } from "@tanstack/react-table";

import {
  DropdownSelection,
  LATEST_VERSION_ID,
} from "src/cases/common/CaseTemplateDropdown";
import { CASES_DEFAULT_SORT } from "src/cases/queries/queues";
import {
  CaseQueueColumnKey,
  CaseQueueColumnSchema,
  CaseQueueColumnSchemaOption,
  CaseQueueColumnSchemas,
  DeserializedCaseQueue,
  DeserializedCaseTemplateVersion,
  DeserializedQueueCase,
  EnrichedDeserializedQueueCase,
} from "src/cases/queues/types";
import { CasePropertiesValue, EnumValue } from "src/clients/case-management";
import {
  JsonApiResourceObjectUnionQueueMemberAttributesQueueTemplateConfigAttributesQueueDefaultTemplateVersionAttributesAny,
  QueueAtomicOperationsRequest,
  QueueCreateAttributes,
  QueueDefaultTemplateVersionUpdateOperation,
} from "src/clients/case-management-service";

export const CASE_QUEUE_ICONS: Record<string, IconProp> = {
  "shield-halved": faShieldHalved,
  "user-lock": faUserLock,
  buildings: faBuilding,
  user: faUser,
  "money-bill-trend-up": faMoneyBillTrendUp,
} as const;

export const getCaseQueueIcon = (icon?: string | null) => {
  if (!icon || !(icon in CASE_QUEUE_ICONS)) return null;
  return CASE_QUEUE_ICONS[icon];
};

const extractDefaultTemplateVersion = (
  queue: DeserializedCaseQueue,
): DropdownSelection | null => {
  const dtv = queue.default_template_version;
  if (!dtv?.template) return null;

  if (dtv.kind === "latest") {
    return { templateId: dtv.template.id, versionId: LATEST_VERSION_ID };
  }

  if (!dtv.template_version?.id) return null;

  return {
    templateId: dtv.template.id,
    versionId: dtv.template_version.id,
  };
};

export const extractInitialFormValues = (queue?: DeserializedCaseQueue) => {
  if (!queue) return;

  return {
    attributes: {
      name: queue.name,
      icon: queue.icon,
    },
    memberIds: queue.members.map(({ user }) => user.id),
    templateIds: queue.template_configs.map(({ template }) => template.id),
    defaultTemplateVersion: extractDefaultTemplateVersion(queue),
  };
};

type CreateQueueAtomicOperationParams = {
  queueId: string;
  attributes: QueueCreateAttributes;
  templateIds: string[];
  memberIds: string[];
  defaultTemplateVersion?: DropdownSelection | null;
};

type QueueAtomicOperationInclude =
  JsonApiResourceObjectUnionQueueMemberAttributesQueueTemplateConfigAttributesQueueDefaultTemplateVersionAttributesAny;

export const DEFAULT_TEMPLATE_VERSION_RESOURCE_ID = "default-template-version";

export const buildDefaultTemplateVersionInclude = (
  selection: DropdownSelection,
): QueueAtomicOperationInclude => {
  const isLatest = selection.versionId === LATEST_VERSION_ID;
  return {
    type: "default_template_versions",
    id: DEFAULT_TEMPLATE_VERSION_RESOURCE_ID,
    attributes: { kind: isLatest ? "latest" : "pinned" },
    relationships: {
      template: { data: { type: "templates", id: selection.templateId } },
      ...(isLatest
        ? {}
        : {
            template_version: {
              data: {
                type: "template_versions",
                id: selection.versionId,
              },
            },
          }),
    },
  } as unknown as QueueAtomicOperationInclude;
};

export const createQueueAtomicOperationIncludes = ({
  memberIds,
  templateIds,
  defaultTemplateVersion,
}: Omit<
  CreateQueueAtomicOperationParams,
  "queueId" | "attributes"
>): QueueAtomicOperationsRequest["included"] => {
  return [
    ...memberIds.map(
      (id) =>
        ({
          type: "queue_members" as const,
          id,
          attributes: { type: "user" },
          relationships: {
            user: { data: { type: "users", id } },
          },
        }) as unknown as QueueAtomicOperationInclude,
    ),
    ...templateIds.map(
      (id) =>
        ({
          type: "template_configs" as const,
          id,
          attributes: { assignment_logic: "self-assign" },
          relationships: {
            template: {
              data: { type: "templates", id },
            },
          },
        }) as unknown as QueueAtomicOperationInclude,
    ),
    ...(defaultTemplateVersion
      ? [buildDefaultTemplateVersionInclude(defaultTemplateVersion)]
      : []),
  ];
};

export const createQueueAtomicOperation = ({
  queueId,
  memberIds,
  attributes,
  templateIds,
  defaultTemplateVersion,
}: CreateQueueAtomicOperationParams): QueueAtomicOperationsRequest["atomic:operations"] => {
  return [
    {
      op: "update",
      ref: {
        type: "queues",
        id: queueId,
        relationship: "attributes",
      },
      data: attributes,
    },
    {
      op: "update",
      ref: {
        type: "queues",
        id: queueId,
        relationship: "template_configs",
      },
      data: templateIds.map((id) => ({
        type: "template_configs",
        id,
      })),
    },
    {
      op: "update",
      ref: {
        type: "queues",
        id: queueId,
        relationship: "members",
      },
      data: memberIds.map((id) => ({
        type: "queue_members",
        id,
      })),
    },
    ...(defaultTemplateVersion !== undefined
      ? [
          {
            op: "update",
            ref: {
              type: "queues",
              id: queueId,
              relationship: "default_template_version",
            },
            data: defaultTemplateVersion
              ? {
                  type: "default_template_versions",
                  id: DEFAULT_TEMPLATE_VERSION_RESOURCE_ID,
                }
              : null,
          } as QueueDefaultTemplateVersionUpdateOperation,
        ]
      : []),
  ];
};

export const RESERVED_PROPERTY_KEYS = ["status", "sub_status"];

const SORTABLE_PROPERTY_TYPES = ["number", "integer", "datetime"] as const;

export const isColumnSortable = (
  columnSchema: CaseQueueColumnSchema,
): boolean =>
  SORTABLE_PROPERTY_TYPES.includes(
    columnSchema.type as (typeof SORTABLE_PROPERTY_TYPES)[number],
  );

export const sortingToApiSortParam = (sorting: SortingState): string => {
  if (sorting.length === 0) return CASES_DEFAULT_SORT;

  const { id: columnId, desc } = sorting[0];
  if (!columnId.includes(".")) return desc ? `-${columnId}` : columnId;

  const [propType, propKey] = columnId.split(".");
  const apiKey = `properties.${propKey}+${propType}`;
  return desc ? `-${apiKey}` : apiKey;
};

/**
 * Flattens predefined enum values into column schema options, expanding a value
 * with sub-values into one option per status/sub-status pair.
 */
const toColumnSchemaOptions = (
  values: EnumValue[],
): CaseQueueColumnSchemaOption[] =>
  values.flatMap((value) => {
    const option = {
      value: value.value,
      displayName: value.display_name || value.value,
      color: value.color ?? undefined,
    };

    if (!value.sub_values?.length) {
      return [option];
    }

    return value.sub_values.map((subValue) => ({
      ...option,
      subValue: {
        value: subValue.value,
        displayName: subValue.display_name || subValue.value,
      },
    }));
  });

const createOptionKey = (option: CaseQueueColumnSchemaOption): string =>
  JSON.stringify([option.value, option.subValue?.value]);

const hasRealDisplayName = (option: CaseQueueColumnSchemaOption): boolean =>
  option.displayName !== option.value;

/**
 * Deduplicates options by value/sub-value pair, preferring options with a real
 * displayName over ones where displayName is just the raw value fallback.
 */
const dedupeColumnSchemaOptions = (
  options: CaseQueueColumnSchemaOption[],
): CaseQueueColumnSchemaOption[] => {
  const optionsByKey = options.reduce<Map<string, CaseQueueColumnSchemaOption>>(
    (byKey, option) => {
      const existingOption = byKey.get(createOptionKey(option));

      if (
        !existingOption ||
        (!hasRealDisplayName(existingOption) && hasRealDisplayName(option))
      ) {
        byKey.set(createOptionKey(option), option);
      }

      return byKey;
    },
    new Map(),
  );

  return Array.from(optionsByKey.values());
};

/**
 *  Extracts a unique set of column schemas from an array of case template versions.
 *
 * This utility processes multiple case template version objects and aggregates
 * all unique case property schemas from their `case_properties` fields into a flat object.
 * A unique schema key (generated by `createColumnKey`) ensures that each schema
 * is only included once, even if it appears in multiple versions.
 *
 */
export const extractQueueColumnSchemas = (
  versions: DeserializedCaseTemplateVersion[],
) => {
  return versions.reduce((columnSchemas, version) => {
    const propertyEntries = Object.entries(version.case_properties);

    propertyEntries.forEach(([propertyKey, schema]) => {
      const propertySchema = schema as CasePropertiesValue;

      const schemaKey = createColumnKey(propertyKey, propertySchema);
      const columnSchema = columnSchemas[schemaKey];

      const withOptions =
        propertySchema.type === "enum" || propertySchema.type === "tags";

      if (columnSchema) {
        columnSchema.template_version_ids.add(version.id);
        // if the schema is enum or tags, we need to merge options
        if (withOptions && columnSchema.type === propertySchema.type) {
          columnSchema.options = dedupeColumnSchemaOptions([
            ...columnSchema.options,
            ...toColumnSchemaOptions(propertySchema.values ?? []),
          ]);
        }
      } else {
        if (withOptions) {
          // create new schema with options
          columnSchemas[schemaKey] = {
            template_version_ids: new Set([version.id]),
            type: propertySchema.type,
            display_name: propertySchema.display_name,
            options: toColumnSchemaOptions(propertySchema.values ?? []),
            __key: propertyKey,
          };
        } else if (propertySchema.type === "entity_ref") {
          columnSchemas[schemaKey] = {
            template_version_ids: new Set([version.id]),
            type: "entity_ref",
            display_name: propertySchema.display_name,
            __key: propertyKey,
            entity_type: propertySchema.entity_type,
          };
        } else if (propertySchema.type === "event_ref") {
          columnSchemas[schemaKey] = {
            template_version_ids: new Set([version.id]),
            type: "event_ref",
            display_name: propertySchema.display_name,
            __key: propertyKey,
            event_type: propertySchema.event_type,
          };
        } else {
          columnSchemas[schemaKey] = {
            template_version_ids: new Set([version.id]),
            type: propertySchema.type,
            display_name: propertySchema.display_name,
            __key: propertyKey,
          };
        }
      }
    });

    return columnSchemas;
  }, {} as CaseQueueColumnSchemas);
};

export const createColumnKey = (
  key: string,
  schema: CasePropertiesValue,
): CaseQueueColumnKey => {
  let schemaKey = `${schema.type}.${key}`;

  if (!RESERVED_PROPERTY_KEYS.includes(key)) {
    const displayName = schema.display_name.toLowerCase().replaceAll(" ", "_");
    schemaKey += `.${displayName}`;
  }

  return schemaKey as CaseQueueColumnKey;
};

/**
 * Enriches a case object by mapping its custom properties onto
 * standardized column keys based on provided column schemas
 */
export const enrichCaseWithColumnValues = (
  caseData: DeserializedQueueCase,
  queueColumnSchemas: CaseQueueColumnSchemas,
) => {
  const enrichedCaseData = {
    ...caseData,
  } as EnrichedDeserializedQueueCase;

  const customProperties = caseData.case_properties;
  const columnKeys = Object.keys(queueColumnSchemas).filter(
    (key): key is CaseQueueColumnKey => !!key,
  );

  for (const columnKey of columnKeys) {
    const columnSchema = queueColumnSchemas[columnKey];
    enrichedCaseData[columnKey] = columnSchema
      ? customProperties[columnSchema.__key]
      : undefined;
  }

  return enrichedCaseData;
};
